import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $walletAddressesStore } from '@lib/store';
import { cn, CurrencyUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api';
import { TopUpWithCryptoModal } from '@widgets';
import { Button } from '@shared/widgets';

interface AccountManipulationsProps {
  className?: string;
  account: AccountResponse;
  mainAccount?: AccountResponse;
  subAccounts?: AccountResponse[];
  selectedAccountId?: number;
  onSelectAccount?: (accountId: number) => void;
}

export const AccountManipulations = (props: AccountManipulationsProps) => {
  const walletAddressesStore = useUnit($walletAddressesStore.store);

  const [showTopUpModalOpen, setShowTopUpModalOpen] = useState<boolean>(false);

  const { t } = useTranslation('accounts');

  useEffect(() => {
    if (props.account.mainAccId != null) return;
    $walletAddressesStore.events.getWalletAddresses({ accountId: props.account.id });
    return () => $walletAddressesStore.events.clear();
  }, [props.account.id, props.account.mainAccId]);

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-2', props.className)}>
      <div className={cn('flex gap-2', 'max-sm:flex-col')}>
        <Button
          className={cn(
            'h-fit rounded border border-light-gray px-4 py-2 text-xs font-semibold uppercase text-light-gray',
            'hover:bg-primary hover:text-white hover:opacity-100',
            props.account.mainAccId === null && props.selectedAccountId === props.account.id && 'bg-primary text-white',
          )}
          disabled={props.mainAccount === undefined}
          onClick={() => props.onSelectAccount?.(props.account.mainAccId === null ? props.account.id : props.account.mainAccId)}
        >
          {CurrencyUtils.getSymbolByCode(props.mainAccount ? props.mainAccount.currency : '')} {t('accountType.main', { ns: 'common' })}
        </Button>
        {props.subAccounts?.map((subAccount) => (
          <Button
            key={subAccount.id}
            className={cn(
              'h-fit rounded border border-light-gray px-4 py-2 text-xs font-semibold uppercase text-light-gray',
              'hover:bg-primary hover:text-white hover:opacity-100',
              props.selectedAccountId === subAccount.id && 'bg-primary text-white',
            )}
            onClick={() => props.onSelectAccount?.(subAccount.id)}
          >
            {CurrencyUtils.getSymbolByCode(subAccount.currency)} {t('accountType.sub', { ns: 'common' })}
          </Button>
        ))}
      </div>
      {props.account.mainAccId === null && walletAddressesStore.data.length > 0 && (
        <Button color='primary' onClick={() => setShowTopUpModalOpen(true)}>
          {t('modals.topUpWithCrypto.title')}
        </Button>
      )}
      <TopUpWithCryptoModal isOpen={showTopUpModalOpen} onClose={() => setShowTopUpModalOpen(false)} accountId={props.account.id} />
    </div>
  );
};
